import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, Heart, Plus, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { DropCountdown } from "./DropCountdown";

export const ProductCard = ({ product, onQuickView, index = 0, className = "" }) => {
  const { add, setOpen } = useCart();
  const { ids, toggle } = useWishlist();
  const hearted = ids.has(product.id);
  const [picking, setPicking] = useState(false);
  const allOut = !!product.sizes && ["S", "M", "L"].every((s) => (product.sizes[s] ?? 0) <= 0);
  return (
    <motion.article
      initial={{ opacity: 0, y: 56 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (index % 4) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group ${className}`}
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative overflow-hidden bg-white/5">
        <div className="aspect-[3/4] w-full overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.07]"
          />
        </div>
        {product.tag && (
          <span className="absolute left-4 top-4 bg-[#0B101E]/90 px-3 py-1.5 text-[0.6rem] font-bold uppercase tracking-[0.26em] text-gold backdrop-blur" data-testid={`product-tag-${product.slug}`}>
            {product.tag}
          </span>
        )}
        <button
          onClick={() => toggle(product)}
          aria-label={hearted ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`}
          data-testid={`wishlist-${product.slug}`}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#0B101E]/70 backdrop-blur transition-colors duration-300 hover:bg-[#0B101E]"
        >
          <Heart className={`h-[18px] w-[18px] transition-colors duration-300 ${hearted ? "fill-burgundy text-burgundy" : "text-cream"}`} strokeWidth={1.6} />
        </button>
        <div className="absolute inset-x-4 bottom-4 translate-y-3 opacity-0 transition-[transform,opacity] duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100">
          <AnimatePresence mode="wait" initial={false}>
            {picking ? (
              <motion.div
                key="sizes"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
                className="flex gap-2"
              >
                {["S", "M", "L"].map((s) => {
                  const out = (product.sizes?.[s] ?? 1) <= 0;
                  return (
                    <button
                      key={s}
                      disabled={out}
                      onClick={() => {
                        add(product, s);
                        setPicking(false);
                        setOpen(true);
                      }}
                      data-testid={`size-pick-${product.slug}-${s.toLowerCase()}`}
                      className={`flex-1 py-3.5 text-[0.72rem] font-bold tracking-[0.2em] backdrop-blur transition-colors duration-300 ${
                        out ? "cursor-not-allowed bg-navy/60 text-cream/40 line-through" : "bg-navy/95 text-cream hover:bg-gold hover:text-[#0B101E]"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPicking(false)}
                  aria-label="Cancel size selection"
                  data-testid={`size-pick-cancel-${product.slug}`}
                  className="flex w-11 items-center justify-center bg-white/95 text-[#0B101E] backdrop-blur transition-colors duration-300 hover:bg-burgundy hover:text-white"
                >
                  <X className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="cta"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
                className="flex gap-2"
              >
                <button
                  onClick={() => (allOut ? onQuickView(product) : setPicking(true))}
                  data-testid={`add-to-cart-${product.slug}`}
                  className={`flex flex-1 items-center justify-center gap-2 py-3.5 text-[0.62rem] font-bold uppercase tracking-[0.24em] backdrop-blur transition-colors duration-300 ${
                    allOut ? "bg-burgundy/90 text-white hover:bg-burgundy" : "bg-navy/95 text-cream hover:bg-burgundy hover:text-white"
                  }`}
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={2} /> {allOut ? "Join the Waitlist" : "Add Elegance to Your Cart"}
                </button>
                <button
                  onClick={() => onQuickView(product)}
                  aria-label={`Quick view ${product.name}`}
                  data-testid={`quick-view-${product.slug}`}
                  className="flex w-12 items-center justify-center bg-white/95 text-[#0B101E] backdrop-blur transition-colors duration-300 hover:bg-gold"
                >
                  <Eye className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="flex items-start justify-between pt-5">
        <div>
          <h3 className="font-serif text-xl leading-tight text-navy">{product.name}</h3>
          <p className="mt-1 text-[0.62rem] font-semibold uppercase tracking-[0.26em] text-navy/50">{product.category_name}</p>
        </div>
        <span className="font-serif text-lg text-gold" data-testid={`product-price-${product.slug}`}>${product.price.toFixed(2)}</span>
      </div>
      {product.drop_ends_at && (
        <div className="mt-2.5">
          <DropCountdown until={product.drop_ends_at} />
        </div>
      )}
    </motion.article>
  );
};
